/**
 * Grid类
 * 管理游戏网格数据
 */

import type { Position, TileValue } from './types';
import { Tile } from './tile';
import { isWithinBounds } from './utils';

export class Grid {
  /** 网格大小 */
  public readonly size: number;
  /** 单元格数组（二维数组展平为一维） */
  private cells: Array<Tile | null>;

  /**
   * 构造函数
   * @param size 网格大小
   */
  constructor(size: number) {
    this.size = size;
    this.cells = new Array(size * size).fill(null);
  }

  /**
   * 清空网格
   */
  public empty(): void {
    this.cells = new Array(this.size * this.size).fill(null);
  }

  /**
   * 获取指定位置的单元格索引
   * @param position 位置
   * @returns 单元格索引
   */
  private getIndex(position: Position): number {
    return position.y * this.size + position.x;
  }

  /**
   * 检查是否有可用的空格
   * @returns 是否有空格
   */
  public cellsAvailable(): boolean {
    return this.cells.some((cell) => cell === null);
  }

  /**
   * 检查指定位置是否可用（在范围内且为空）
   * @param position 位置
   * @returns 是否可用
   */
  public cellAvailable(position: Position): boolean {
    if (!isWithinBounds(position, this.size)) {
      return false;
    }
    return this.cellContent(position) === null;
  }

  /**
   * 获取指定位置的方块
   * @param position 位置
   * @returns 方块实例或null
   */
  public cellContent(position: Position): Tile | null {
    if (!isWithinBounds(position, this.size)) {
      return null;
    }
    return this.cells[this.getIndex(position)] ?? null;
  }

  /**
   * 插入方块到网格
   * @param tile 方块实例
   */
  public insertTile(tile: Tile): void {
    if (isWithinBounds(tile.position, this.size)) {
      this.cells[this.getIndex(tile.position)] = tile;
    }
  }

  /**
   * 从网格移除方块
   * @param tile 方块实例
   */
  public removeTile(tile: Tile): void {
    if (isWithinBounds(tile.position, this.size)) {
      this.cells[this.getIndex(tile.position)] = null;
    }
  }

  /**
   * 获取所有可用的空位置
   * @returns 位置数组
   */
  public availableCells(): Position[] {
    const cells: Position[] = [];
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        const position: Position = { x, y };
        if (this.cellAvailable(position)) {
          cells.push(position);
        }
      }
    }
    return cells;
  }

  /**
   * 获取所有方块
   * @returns 方块数组
   */
  public getAllTiles(): Tile[] {
    const tiles: Tile[] = [];
    for (const cell of this.cells) {
      if (cell !== null) {
        tiles.push(cell);
      }
    }
    return tiles;
  }

  /**
   * 遍历每个单元格
   * @param callback 回调函数
   */
  public eachCell(callback: (x: number, y: number, tile: Tile | null) => void): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        const tile = this.cellContent({ x, y });
        callback(x, y, tile);
      }
    }
  }

  /**
   * 序列化网格数据
   * @returns 序列化后的数据
   */
  public serialize(): {
    size: number;
    cells: Array<{ position: Position; value: TileValue } | null>;
  } {
    return {
      size: this.size,
      cells: this.cells.map((cell) => (cell ? cell.serialize() : null)),
    };
  }
}
